import React, { Component } from "react";
import { withRouter } from "react-router-dom";

import Button from "@/components/UI/Button";
import dayjs from "@/lib/helpers/dayjs";
import { FeatureFlagEnum } from "@/features/feature-flag/constants";
import { FeatureGate } from "@/features/feature-flag/components/FeatureGate";
import { sharedRequests } from "@/api/(requests)/shared";

import AssessmentActivity, { GenericAssessmentActivity } from "./AssessmentActivity";
import BillingChange from "./BillingChange";
import {
  CallCancelledActivity,
  CallCompletedActivity,
  CallRescheduledActivity,
  CallScheduledActivity,
  InstantCallRequestedActivity,
  PatientNoShowActivity,
  SchedulingRestrictionActivity,
} from "./ScheduleActivity";
import {
  EmailUpdatedActivity,
  PasswordResetActivity,
  PracticeChangedActivity,
} from "./AdminActivity";
import { MemberActivatedActivity, MemberInactivatedActivity } from "./MemberActivity";
import { ReportCompletedActivity, ReportGeneratedActivity } from "./ReportActivity";
import { WaitListAddedActivity, WaitListRemovedActivity } from "./WaitListActivity";

import { patientIdChange } from "./index";

class ActivityFeed extends Component {
  constructor(props) {
    super(props);
    let pageSize = 10;

    this.state = {
      activities: props.data ? props.data["activities"] : [],
      activitiesToShow: 5,
      pageSize: pageSize,
      patientId: this.props.match.params.patientId,
    };
  }

  componentDidMount() {
    if (this.state.activities.length === 0) {
      let urlParams = { patientId: this.state.patientId };

      sharedRequests
        .fetch_patient_activity_feed({ context: { showLoader: false }, urlParams })
        .then((response) => {
          if (response.results) {
            this.setState({ activities: response.results });
            const data = { ...this.props.data };

            data["activities"] = response.results;
            if (this.props.updateState) {
              this.props.updateState({ timelineData: data });
            }
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }

  componentDidUpdate(prevProps) {
    patientIdChange.bind(this)(prevProps);
    // if the parents activities have changed update this components state
    // this is used when the parent re-fetches activities after a new activity has been added
    const parentActivities = this.props?.data?.activities;

    if (parentActivities && parentActivities.length !== this.state.activities.length) {
      this.setState({ activities: parentActivities });
    }
  }

  showMore = () =>
    this.setState({
      activitiesToShow: this.state.activitiesToShow + this.state.pageSize,
    });

  getShowMoreButton = () => {
    return (
      <div className="d-flex align-items-center py-4 tw:justify-center">
        <Button className="Btn Btn--pri ShowMoreBtn ml-4 text-center" onClick={this.showMore}>
          Show More
        </Button>
      </div>
    );
  };

  activityFeedEntryFactory = (activity) => {
    const keyContent = activity.key_content || {};

    switch (activity.name) {
      case "Report Generated":
        return (
          <ReportGeneratedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Report Completed":
        return (
          <ReportCompletedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Clinical Assessment Completed":
        return (
          <AssessmentActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Video Consultation Scheduled":
        return (
          <CallScheduledActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Call Completed":
        return (
          <CallCompletedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Video Consultation Cancelled":
        return (
          <CallCancelledActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Video Consultation Rescheduled":
        return (
          <CallRescheduledActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Instant Video Call Requested":
        return (
          <InstantCallRequestedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Patient No-Show":
        return (
          <PatientNoShowActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Member Activation":
        return (
          <MemberActivatedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Member Inactivation":
        return (
          <MemberInactivatedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Email Updated":
        return (
          <EmailUpdatedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Patient Practice Changed":
        return (
          <PracticeChangedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Automatic Payment Changed":
        return (
          <BillingChange keyContent={keyContent} localCreatedTime={activity.localCreatedTime} />
        );

      case "Waitlisted":
        return (
          <WaitListAddedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Removed from waitlist":
        return (
          <WaitListRemovedActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Admin Password Reset":
        return (
          <PasswordResetActivity
            keyContent={keyContent}
            localCreatedTime={activity.localCreatedTime}
          />
        );

      case "Scheduling Restriction Change":
        return (
          <FeatureGate
            defaultFlagValue={true}
            disabledFallback={<></>}
            flagName={FeatureFlagEnum.restrictPatientScheduling}
          >
            <SchedulingRestrictionActivity
              keyContent={keyContent}
              localCreatedTime={activity.localCreatedTime}
            />
          </FeatureGate>
        );

      case "Assessment":
        return (
          <GenericAssessmentActivity
            keyContent={keyContent}
            timestamp={activity.localCreatedTime}
          />
        );

      default:
        return <></>;
    }
  };

  render() {
    return (
      <div>
        {this.state.activities.slice(0, this.state.activitiesToShow).map((activity, i) => {
          /** Date needs to be parsed to correct format first, due to the differences between moment
           * https://day.js.org/docs/en/parse/string-format#differences-to-moment
           */
          activity.localCreatedTime = dayjs
            .utc(activity.timestamp, "MM/DD/YYYY HH:mm:ss")
            .local()
            .format("MM/DD/YYYY hh:mm a");

          return <div key={activity.entity_id + i}>{this.activityFeedEntryFactory(activity)}</div>;
        })}
        {this.state.activities &&
          this.state.activitiesToShow < this.state.activities.length &&
          this.getShowMoreButton()}
      </div>
    );
  }
}

export default withRouter(ActivityFeed);
